import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { useAuth } from './auth';
import { UnauthorizedError } from './client';

/**
 * Единственное место, где обрабатывается истечение сессии.
 * 401 из ЛЮБОГО запроса гасит состояние аутентификации — RequireAuth тут же
 * уводит на /login, запомнив текущий маршрут (Д-Ф4).
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const { expire } = useAuth();

  const client = useMemo(() => {
    const onError = (error: unknown) => {
      if (error instanceof UnauthorizedError) expire();
    };

    return new QueryClient({
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          gcTime: 5 * 60_000,
          refetchOnWindowFocus: true,
          retry: (failureCount, error) => {
            // Повторять запрос без сессии бессмысленно и вредно:
            // это удлиняет путь до экрана входа
            if (error instanceof UnauthorizedError) return false;
            return failureCount < 2;
          },
        },
      },
    });
  }, [expire]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
